import { createFileRoute } from '@tanstack/react-router'
import { Plus, Trash2 } from 'lucide-react'

import { Product, ProductGroup } from '@/api/admin-products-types'
import { useUpdateProduct, useUpdateProductGroup } from '@/api/api-mutations'
import { PricingMethod } from '@/api/common-types'
import { useGetProduct } from '@/api/react-query'
import { Button } from '@/components/base/button'
import { Drawer } from '@/components/base/drawer'
import { Heading } from '@/components/base/heading'
import { LoadingSpinner } from '@/components/base/loading-spinner'
import { toast } from '@/components/base/toast'
import { FormContainer, FormFieldGroup, FormLabel, useAppForm } from '@/components/form/form'
import { RouteModal, RouteModalFormBlocker } from '@/components/route-modal'
import { useRouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'

export const Route = createFileRoute('/_authenticated/_general/product-groups/$groupUuid/edit')({
	component: ProductGroupEdit
})

function ProductGroupEdit() {
	const { groupUuid } = Route.useParams()

	return (
		<RouteModal
			prev='..'
			asDrawer
			requiresData
			loader={async api => await api.getProductGroup({ path: { groupUuid } })}
		>
			{data => <ProductGroupEditComponent productGroup={data} />}
		</RouteModal>
	)
}

function ProductGroupEditComponent({ productGroup }: { productGroup: ProductGroup }) {
	const { L } = useI18n()
	const { groupUuid } = Route.useParams()
	const { handleSuccess } = useRouteModal()

	const updateProductGroup = useUpdateProductGroup()

	const form = useAppForm({
		defaultValues: {
			name: productGroup.name
		},
		onSubmit: async ({ value }) => {
			const result = await updateProductGroup({ path: { groupUuid }, body: value })
			if (result.isSuccess) {
				toast.success(L.ProductGroup.EditSuccessToast({ name: value.name }))
				handleSuccess()
			} else {
				toast.error((result.response as any)?.message ?? L.Error.SomethingWentWrong())
			}
		}
	})

	return (
		<form.AppForm>
			<RouteModalFormBlocker form={form as any} />
			<Drawer.Header>
				<Drawer.Title asChild>
					<Heading>{L.Product.EditHeader()}</Heading>
				</Drawer.Title>
			</Drawer.Header>
			<Drawer.Body className='flex-1 overflow-auto'>
				<FormContainer>
					<FormFieldGroup>
						<form.AppField name='name' children={field => <field.TextField label={L.Fields.Name()} />} />
					</FormFieldGroup>
				</FormContainer>
			</Drawer.Body>
			<Drawer.Footer>
				<div className='flex items-center justify-end gap-x-2'>
					<Drawer.Close asChild>
						<Button variant='secondary' size='small'>
							{L.General.Cancel()}
						</Button>
					</Drawer.Close>

					<form.SaveButton />
				</div>
			</Drawer.Footer>
		</form.AppForm>
	)
}
